import { Icon } from '@pypestream/design-system';
import { FC, useState } from 'react';

import { headerColorFor } from '../canvas-view/agent-node-card';
import type {
  AgentDiff,
  DiffCounts,
  NodeChange,
  NodeChangeKind,
} from './solution-diff';
import type { ActionFileChange } from './action-file-diff';

const KIND_ICON: Record<NodeChangeKind, 'plus' | 'pencil' | 'minus'> = {
  added: 'plus',
  edited: 'pencil',
  removed: 'minus',
};

const KIND_CLASS: Record<NodeChangeKind, string> = {
  added: 'add',
  edited: 'edit',
  removed: 'rm',
};

/** `+ 5  ✎ 12  − 3`, skipping zeros. */
export const CountChips: FC<{ counts: DiffCounts; showZero?: boolean }> = ({
  counts,
  showZero,
}) => (
  <span className="c-deploy-counts">
    {(['added', 'edited', 'removed'] as NodeChangeKind[]).map((kind) =>
      counts[kind] || showZero ? (
        <span
          key={kind}
          className={`c-deploy-count c-deploy-count--${KIND_CLASS[kind]}`}
        >
          <Icon name={KIND_ICON[kind]} size="xsmall" />
          {counts[kind]}
        </span>
      ) : null
    )}
  </span>
);

export const KindTag: FC<{ kind: NodeChangeKind }> = ({ kind }) => (
  <span
    className={`c-deploy-kind c-deploy-kind--${KIND_CLASS[kind]}`}
    aria-label={kind}
  >
    <Icon name={KIND_ICON[kind]} size="xsmall" />
  </span>
);

export const agentBarColor = (agent: AgentDiff) =>
  agent.isUngrouped
    ? undefined
    : (headerColorFor(agent.color ?? null) ?? undefined);

/** One changed node on a line: tag, number, name. */
export const ChangeLine: FC<{ change: NodeChange }> = ({ change }) => (
  <li className="c-deploy-change-line">
    <KindTag kind={change.kind} />
    <span className="c-deploy-node-number">{change.nodeNumber}</span>
    <span
      className={`c-deploy-change-line__name${
        change.kind === 'removed' ? ' is-removed' : ''
      }`}
    >
      {change.nodeName || 'Untitled node'}
    </span>
  </li>
);

/** "What's changing, by agent": one row per agent, expanding to its nodes. */
export const AgentChanges: FC<{ agents: AgentDiff[] }> = ({ agents }) => {
  const [open, setOpen] = useState<string | null>(null);
  if (!agents.length) return null;
  return (
    <div className="c-deploy-agents" data-testid="bobcat-deploy-agent-changes">
      {agents.map((agent) => {
        const isOpen = open === agent.name;
        return (
          <div key={agent.name} className="c-deploy-agents__item">
            <button
              type="button"
              className="c-deploy-agents__row"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : agent.name)}
            >
              <span
                className="c-deploy-agents__dot"
                style={{ background: agentBarColor(agent) }}
              />
              <span className="c-deploy-agents__name">{agent.name}</span>
              <CountChips counts={agent} />
              <Icon
                name={isOpen ? 'chevron-down' : 'chevron-right'}
                size="xsmall"
              />
            </button>
            {isOpen && (
              <ul className="c-deploy-agents__nodes">
                {agent.changes.map((change) => (
                  <ChangeLine
                    key={`${change.kind}:${change.nodeNumber}:${change.nodeName}`}
                    change={change}
                  />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * The action code a publish changes — scripts from the Actions page — one row
 * per file, opening to its changed lines.
 */
export const ActionChanges: FC<{ changes: ActionFileChange[] }> = ({
  changes,
}) => {
  const [open, setOpen] = useState<string | null>(null);
  if (!changes.length) return null;
  return (
    <div className="c-deploy-agents" data-testid="bobcat-deploy-action-changes">
      {changes.map((c) => {
        const isOpen = open === c.path;
        const folder = c.path.includes('/')
          ? c.path.slice(0, c.path.lastIndexOf('/') + 1)
          : '';
        return (
          <div key={c.path} className="c-deploy-agents__item">
            <button
              type="button"
              className="c-deploy-agents__row"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : c.path)}
              title={c.path}
            >
              <KindTag kind={c.change} />
              <span className="c-deploy-agents__name">
                {folder && (
                  <span className="c-deploy-action__folder">{folder}</span>
                )}
                {c.name}
              </span>
              <span className="c-deploy-action__lines">
                {c.added > 0 && (
                  <span className="c-deploy-action__add">+{c.added}</span>
                )}
                {c.removed > 0 && (
                  <span className="c-deploy-action__rm">−{c.removed}</span>
                )}
              </span>
              <Icon
                name={isOpen ? 'chevron-down' : 'chevron-right'}
                size="xsmall"
              />
            </button>
            {isOpen &&
              (c.lines.length ? (
                <pre className="c-deploy-action__code">
                  {c.lines.map((l, i) => (
                    <span
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className={`c-deploy-action__line is-${l.kind}`}
                    >
                      <span aria-hidden="true">
                        {l.kind === 'add' ? '+' : l.kind === 'rm' ? '−' : ' '}
                      </span>
                      {l.text || ' '}
                    </span>
                  ))}
                </pre>
              ) : (
                <p className="c-deploy-action__big">
                  Too long to show line by line.
                </p>
              ))}
          </div>
        );
      })}
    </div>
  );
};
